import { type AxiosResponse } from "axios";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { secondsFromNow } from "@/utils/time";

import type { ApiErrorResponse } from "@/types/api";

type IRateLimitContext = {
  getTimeRemaining(key: string): number;
  onSuccess(data: AxiosResponse, key: string): void;
  onError(error: ApiErrorResponse, key: string): void;
};

const RateLimitContext = createContext<any>({});

interface RateLimitProviderProps {
  children: React.ReactNode;
}

const RateLimitProvider = ({ children }: RateLimitProviderProps) => {
  const [timeRemaining, setTimeRemaining] = useState<Record<string, number>>(
    {},
  );

  const onSuccess = (data: AxiosResponse, key: string) => {
    const headerLimit = Number(data.headers["x-ratelimit-remaining"] || 0);
    const headerReset = data?.headers["x-ratelimit-reset"] || null;

    if (headerLimit === 0 && headerReset) {
      const secondsFromReset = secondsFromNow(headerReset);
      setTimeRemaining((prev) => ({ ...prev, [key]: secondsFromReset }));
    } else {
      setTimeRemaining((prev) => ({ ...prev, [key]: 60 }));
    }
  };

  const onError = (error: ApiErrorResponse, key: string) => {
    const headerTimeRemaining = Number(
      error.response?.headers["x-ratelimit-seconds-remaining"] || 0,
    );
    const headerLimit = Number(
      error.response?.headers["x-ratelimit-remaining"] || 0,
    );
    const headerReset = error.response?.headers["x-ratelimit-reset"] || null;

    if (headerLimit === 0 && headerReset) {
      const secondsFromReset = secondsFromNow(headerReset);
      setTimeRemaining((prev) => ({ ...prev, [key]: secondsFromReset }));
    } else {
      setTimeRemaining((prev) => ({ ...prev, [key]: headerTimeRemaining }));
    }
  };

  const getTimeRemaining = (key: string) => {
    return timeRemaining[key] || 0;
  };

  useEffect(() => {
    const haveTimeRemaining = Object.keys(timeRemaining).some(
      (key) => timeRemaining[key] > 0,
    );

    if (!haveTimeRemaining) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        const obj = { ...prev };

        for (const key in prev) {
          obj[key] = obj[key] - 1;
          if (obj[key] <= 0) delete obj[key];
        }

        return obj;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeRemaining]);

  const value = useMemo(
    () => ({
      getTimeRemaining,
      onError,
      onSuccess,
    }),
    [timeRemaining],
  );

  return (
    <RateLimitContext.Provider value={value}>
      {children}
    </RateLimitContext.Provider>
  );
};

const useRateLimitContext = (): IRateLimitContext =>
  useContext(RateLimitContext);

export { RateLimitProvider, useRateLimitContext };
